// 


// src/app/modules/Incidents/pages/Incidents.jsx
import React, { useEffect, useState, useRef } from "react";
import { shallowEqual, useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import * as actions from "../../Dashbord/_redux/dashboard/Actions";
import { IncidentsTable } from "../database";
import { CreateIncidentModal } from "../components/CreateIncidentModal";
import {
  AlertTriangle, Shield, CheckCircle, Activity,
  Filter, Search, XCircle, Radio,
} from 'lucide-react';
import './incidents.scss';

// ── Schema-compat helpers ─────────────────────────────────────────────────────

// New schema active statuses (partner API)
const NEW_ACTIVE = ['created', 'dispatching', 'assigned', 'responding', 'on_scene', 'escalated', 'unresponsive'];
// Old schema active status
const OLD_ACTIVE = ['active', 'investigating'];

const isActiveStatus   = (s) => NEW_ACTIVE.includes(s) || OLD_ACTIVE.includes(s);
const isResolvedStatus = (s) => s === 'resolved';

// ─────────────────────────────────────────────────────────────────────────────

export function Incidents() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isMountedRef = useRef(true);

  const { org, incidents, incidentsMeta, loading } = useSelector(
    (state) => ({
      org:           state.auth.organization,
      incidents:     state.dashboard.incidents,
      incidentsMeta: state.dashboard.incidentsMeta,
      loading:       state.dashboard.listLoading,
    }),
    shallowEqual
  );

  const [currentPage,     setCurrentPage]     = useState(1);
  const [perPage]                             = useState(10);
  const [statusFilter,    setStatusFilter]    = useState('');
  const [severityFilter,  setSeverityFilter]  = useState('');
  const [typeFilter,      setTypeFilter]      = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);

  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  useEffect(() => {
    if (org?.organization_id) {
      fetchIncidents();
      fetchStats();
    }
  }, [currentPage, statusFilter, severityFilter, typeFilter, org?.organization_id]);

  function fetchIncidents() {
    if (!isMountedRef.current) return;
    const params = { page: currentPage, limit: perPage, organization_id: org.organization_id };
    if (statusFilter)   params.status   = statusFilter;
    if (severityFilter) params.severity = severityFilter;
    if (typeFilter)     params.type     = typeFilter;
    dispatch(actions.fetchAllData('incidents', org.organization_id, params));
  }

  function fetchStats() {
    if (!isMountedRef.current) return;
    dispatch(actions.fetchAllData('incident_stats', org.organization_id));
  }

  function handleViewDetails(incidentId) {
    navigate(`/incidents/${incidentId}`);
  }

  // Stats work across both old schema (active/investigating/resolved) and
  // new partner schema (created/dispatching/assigned/.../resolved)
  const list = incidents || [];

  const stats = [
    {
      icon: <Radio size={16} />, bg: '#FEF2F2', color: '#DC2626',
      label: 'Active',
      value: list.filter(i => isActiveStatus(i.status)).length,
    },
    {
      icon: <Shield size={16} />, bg: '#FFFBEB', color: '#D97706',
      label: 'Escalated',
      value: list.filter(i => i.status === 'escalated' || i.status === 'investigating').length,
    },
    {
      icon: <CheckCircle size={16} />, bg: '#F0FDF4', color: '#16a34a',
      label: 'Resolved',
      value: list.filter(i => isResolvedStatus(i.status)).length,
    },
    {
      icon: <Activity size={16} />, bg: '#F0EEFF', color: '#5341C4',
      label: 'Total',
      value: incidentsMeta?.totalResults || 0,
    },
  ];

  return (
    <>
      <div className="incidents-page">

        {/* ── Header ────────────────────────────────────────────────────── */}
        <div className="ac-header">
          <div className="ac-header__left">
            <h1>Incidents</h1>
            <p>Emergency response &amp; tracking</p>
          </div>
        </div>

        {/* ── Stats strip ───────────────────────────────────────────────── */}
        <div className="ac-card">
          <div className="ac-code-stats" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
            {stats.map((s, i) => (
              <div key={i} className="ac-code-stat">
                <div className="ac-code-stat__icon" style={{ background: s.bg, color: s.color }}>
                  {s.icon}
                </div>
                <div>
                  <div className="ac-code-stat__value">{s.value}</div>
                  <div className="ac-code-stat__label">{s.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── Filters ───────────────────────────────────────────────────── */}
        <div className="ac-card">
          <div className="ac-card__body" style={{ padding: '11px 16px' }}>
            <div className="ac-filters">

              <div className="ac-search">
                <Search size={14} />
                <input placeholder="Search incidents…" />
              </div>

              {/* Status — includes both old and new schema values */}
              <select
                className="ac-select"
                value={statusFilter}
                onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
              >
                <option value="">All Status</option>
                <optgroup label="Active">
                  <option value="active">Active (legacy)</option>
                  <option value="created">Created</option>
                  <option value="dispatching">Dispatching</option>
                  <option value="assigned">Assigned</option>
                  <option value="responding">Responding</option>
                  <option value="on_scene">On Scene</option>
                </optgroup>
                <optgroup label="Escalated">
                  <option value="escalated">Escalated</option>
                  <option value="unresponsive">Unresponsive</option>
                  <option value="investigating">Investigating (legacy)</option>
                </optgroup>
                <optgroup label="Closed">
                  <option value="resolved">Resolved</option>
                  <option value="cancelled">Cancelled</option>
                  <option value="false_alarm">False Alarm (legacy)</option>
                </optgroup>
              </select>

              <select
                className="ac-select"
                value={severityFilter}
                onChange={(e) => { setSeverityFilter(e.target.value); setCurrentPage(1); }}
              >
                <option value="">All Severities</option>
                <option value="critical">Critical</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>

              {/* Type covers both old (medical/fire/crime) and new (sos/vehicle/location_threat) */}
              <select
                className="ac-select"
                value={typeFilter}
                onChange={(e) => { setTypeFilter(e.target.value); setCurrentPage(1); }}
              >
                <option value="">All Types</option>
                <optgroup label="Partner API">
                  <option value="sos">SOS</option>
                  <option value="vehicle">Vehicle</option>
                  <option value="location_threat">Location Threat</option>
                  <option value="medical">Medical</option>
                </optgroup>
                <optgroup label="Legacy">
                  <option value="fire">Fire</option>
                  <option value="crime">Crime</option>
                  <option value="accident">Accident</option>
                  <option value="security_threat">Security Threat</option>
                  <option value="other">Other</option>
                </optgroup>
              </select>

              <button
                className="ac-btn ac-btn--secondary"
                onClick={() => { setStatusFilter(''); setSeverityFilter(''); setTypeFilter(''); setCurrentPage(1); }}
              >
                <XCircle size={13} />Reset
              </button>

              <button
                className="ac-apply-btn"
                onClick={() => { setCurrentPage(1); fetchIncidents(); }}
              >
                <Filter size={13} />Apply
              </button>

            </div>
          </div>
        </div>

        {/* ── Table ─────────────────────────────────────────────────────── */}
        <div className="ac-card">
          <div className="ac-card__header">
            <div className="ac-card__title">
              <AlertTriangle size={14} />Incident Log
            </div>
            <span className="ac-card__meta">
              {incidentsMeta?.totalResults || 0} total incidents
            </span>
          </div>
          <div className="ac-table-wrap">
            <IncidentsTable
              incidents={list}
              loading={loading}
              onViewDetails={handleViewDetails}
              currentPage={currentPage}
              perPage={perPage}
              totalCount={incidentsMeta?.totalResults || 0}
              onPageChange={setCurrentPage}
            />
          </div>
        </div>

      </div>

      <CreateIncidentModal
        show={showCreateModal}
        onHide={() => setShowCreateModal(false)}
        organizationId={org?.organization_id}
        onSuccess={() => { setCurrentPage(1); fetchIncidents(); fetchStats(); }}
      />
    </>
  );
}